import { useState } from "react";
import { downloadProtectedFile } from "@/lib/auth";
import { Download } from "lucide-react";
import { toast } from "sonner";

// Read-only summary of a quotation, opened from the Quotations list.
// Mirrors the layout of QuotationBuilder's print output so the on-screen
// view matches what gets downloaded.
export default function QuotationView({ quote: q }) {
  const [downloading, setDownloading] = useState(false);
  const sym = q.currency_symbol || "₹";
  const fmt = (n) => sym + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
  const showExw = q.show_exworks !== false;
  const hasTax = (q.tax_rate || 0) > 0;

  const download = async () => {
    setDownloading(true);
    try {
      await downloadProtectedFile(`/quotations/${q.id}/pdf`, `Quotation_${q.quote_no.replace(/\//g, "-")}.pdf`);
    } catch (e) {
      toast.error("Couldn't download the quotation PDF", { description: e.response?.data?.detail || e.message });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="quotation-view">
      <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr" }}>
        <div className="card-ledger" style={{ padding: 16, background: "var(--surface-2)" }}>
          <div className="overline" style={{ fontSize: 10 }}>Bill To</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginTop: 6 }}>{q.customer_snapshot?.company || "—"}</div>
          <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>{q.customer_snapshot?.contact}</div>
        </div>
        <div className="card-ledger" style={{ padding: 16 }}>
          <div className="overline" style={{ fontSize: 10 }}>Currency</div>
          <div className="font-mono-data" style={{ marginTop: 6, fontSize: 14 }}>{sym} · {q.currency || "INR"}</div>
        </div>
        <div className="card-ledger" style={{ padding: 16 }}>
          <div className="overline" style={{ fontSize: 10 }}>Date</div>
          <div className="font-mono-data" style={{ marginTop: 6, fontSize: 14 }}>{fmtDate(q.date)}</div>
        </div>
        <div className="card-ledger" style={{ padding: 16 }}>
          <div className="overline" style={{ fontSize: 10 }}>Valid Until</div>
          <div className="font-mono-data" style={{ marginTop: 6, fontSize: 14 }}>{fmtDate(q.valid_until)}</div>
        </div>
      </div>

      <div className="card-ledger overflow-hidden mb-4" data-testid="quotation-view-items">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--surface-2)" }}>
              {["#", "Product", "Qty", "FOB", "CIF", ...(showExw ? ["Ex-Works"] : [])].map((h, i) => (
                <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 10px", textAlign: i <= 1 ? "left" : "right", borderBottom: "1px solid var(--border)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(q.items || []).map((row, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid var(--border)" }}>
                <td style={{ padding: "10px", fontSize: 13, color: "var(--text-2)" }}>{idx + 1}</td>
                <td style={{ padding: "10px", fontSize: 13 }}>{row.product || "—"}</td>
                <td className="font-mono-data" style={{ padding: "10px", fontSize: 13, textAlign: "right" }}>{row.quantity} {row.unit}</td>
                <td className="font-mono-data" style={{ padding: "10px", fontSize: 13, textAlign: "right" }}>{fmt(row.fob_price)}</td>
                <td className="font-mono-data" style={{ padding: "10px", fontSize: 13, textAlign: "right" }}>{fmt(row.cif_price)}</td>
                {showExw && <td className="font-mono-data" style={{ padding: "10px", fontSize: 13, textAlign: "right" }}>{fmt(row.ex_works_price)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-6 mb-6 flex-wrap" data-testid="quotation-view-totals">
        {[
          ["FOB Total", q.fob_total, true, true],
          ["CIF Total", q.cif_total, false, true],
          ["Ex-Works Total", q.exworks_total, false, showExw],
          ...(hasTax ? [[q.tax_label || "GST", q.tax_amount, false, true]] : []),
          ...(hasTax ? [["Grand Total", q.grand_total, true, true]] : []),
        ].filter(([, , , show]) => show).map(([label, val, primary]) => (
          <div key={label} style={{ textAlign: "right" }}>
            <div className="overline" style={{ fontSize: 10 }}>{label}</div>
            <div className="font-mono-data" style={{ fontSize: 18, fontWeight: 700, color: primary ? "var(--primary)" : "var(--text)", marginTop: 4 }}>{fmt(val)}</div>
          </div>
        ))}
      </div>

      {(q.payment_terms || q.fixed_terms?.length || q.notes) && (
        <div className="card-ledger mb-6" style={{ padding: 16 }} data-testid="quotation-view-terms">
          {q.payment_terms && (
            <>
              <div className="overline" style={{ fontSize: 10, marginBottom: 6 }}>Payment Terms</div>
              <div style={{ fontSize: 13, whiteSpace: "pre-wrap", marginBottom: 16 }}>{q.payment_terms}</div>
            </>
          )}
          {(q.fixed_terms?.length > 0 || q.notes) && (
            <>
              <div className="overline" style={{ fontSize: 10, marginBottom: 10 }}>Terms & Conditions</div>
              <ol style={{ paddingLeft: 22, margin: 0 }}>
                {(q.fixed_terms || []).map((t, i) => (
                  <li key={`fixed-${i}`} style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6, marginBottom: 4 }}>{t}</li>
                ))}
                {(q.notes || "").split("\n").filter(Boolean).map((t, i) => (
                  <li key={`note-${i}`} style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6, marginBottom: 4 }}>{t}</li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={download} disabled={downloading} className="btn-primary" data-testid="quote-view-download">
          <Download size={15} /> {downloading ? "Downloading…" : "Download PDF"}
        </button>
      </div>
    </div>
  );
}
